import type { DashboardLayout } from "../../../../types/dashboardLayout";
import { DASHBOARD_LAYOUT_VERSION } from "../../../../types/dashboardLayout";
import {
  activateDashboardLayout,
  createDashboardLayout,
  deleteDashboardLayout,
  listDashboardLayouts,
  type DashboardLayoutsListResponse,
  updateDashboardLayout,
} from "../../../../services/dashboardLayoutsApi";
import {
  dashboardLayoutStorageKeys,
  type DashboardLayoutAccountScope,
  isDashboardLayoutAccountScopeReady,
} from "../../../../types/dashboardLayoutAccountScope";
import { DASHBOARD_DEFAULT_LAYOUT_ID } from "./dashboardCardRegistry";
import { sanitizeLayout, serializeDashboardLayoutItemsForApi } from "./dashboardLayoutValidation";

function findRemoteDefaultLayout(remoteLayouts: DashboardLayout[]): DashboardLayout | undefined {
  return remoteLayouts.find((layout) => layout.isDefault);
}

function isLocalDefaultLayout(layout: DashboardLayout): boolean {
  return layout.isDefault || layout.id === DASHBOARD_DEFAULT_LAYOUT_ID;
}

function ensureDefaultLayoutIdMap(
  idMap: Record<string, string>,
  remoteLayouts: DashboardLayout[]
): void {
  const remoteDefault = findRemoteDefaultLayout(remoteLayouts);
  if (remoteDefault?.id) {
    idMap[DASHBOARD_DEFAULT_LAYOUT_ID] = remoteDefault.id;
  }
}

function resolveLayoutRemoteId(
  layout: DashboardLayout,
  idMap: Record<string, string>,
  remoteLayouts: DashboardLayout[]
): string | null {
  if (isLocalDefaultLayout(layout)) {
    ensureDefaultLayoutIdMap(idMap, remoteLayouts);
    return idMap[DASHBOARD_DEFAULT_LAYOUT_ID] ?? null;
  }
  if (idMap[layout.id]) return idMap[layout.id];
  if (layout.id.startsWith("layout_")) return null;
  return layout.id;
}

function layoutUpdatedAtMs(layout: DashboardLayout): number {
  const parsed = Date.parse(layout.updatedAt ?? "");
  return Number.isFinite(parsed) ? parsed : 0;
}

function visibleLayoutItemCount(layout: DashboardLayout): number {
  return layout.items.filter((item) => item.visible).length;
}

/** Prefer the layout with the latest `updatedAt`; tie goes to local. */
export function pickNewerDashboardLayout(
  localLayout: DashboardLayout,
  remoteLayout: DashboardLayout
): DashboardLayout {
  const localMs = layoutUpdatedAtMs(localLayout);
  const remoteMs = layoutUpdatedAtMs(remoteLayout);
  if (remoteMs > localMs) {
    const picked = { ...remoteLayout };
    // Server timestamps can advance while list payloads still carry stale/empty items.
    if (visibleLayoutItemCount(remoteLayout) < visibleLayoutItemCount(localLayout)) {
      picked.items = localLayout.items.map((item) => ({ ...item }));
    }
    return picked;
  }
  return localLayout;
}

function isDefaultLayoutCandidate(
  layout: DashboardLayout,
  remoteDefault?: DashboardLayout
): boolean {
  return (
    isLocalDefaultLayout(layout) ||
    Boolean(remoteDefault && layout.id === remoteDefault.id)
  );
}

function upsertCanonicalDefaultLayout(
  merged: DashboardLayout[],
  candidate: DashboardLayout
): void {
  const canonical: DashboardLayout = {
    ...candidate,
    id: DASHBOARD_DEFAULT_LAYOUT_ID,
    isDefault: true,
  };
  const existingIndex = merged.findIndex(
    (layout) => layout.id === DASHBOARD_DEFAULT_LAYOUT_ID
  );
  if (existingIndex >= 0) {
    merged[existingIndex] = pickNewerDashboardLayout(merged[existingIndex], canonical);
    return;
  }
  merged.push(canonical);
}

/** Collapse duplicate default rows (local `default`, remote uuid, etc.) into one canonical layout. */
export function dedupeDashboardLayouts(layouts: DashboardLayout[]): DashboardLayout[] {
  if (layouts.length <= 1) return layouts;

  const remoteDefault = findRemoteDefaultLayout(layouts);
  const defaults: DashboardLayout[] = [];
  const nonDefaults: DashboardLayout[] = [];

  for (const layout of layouts) {
    if (isDefaultLayoutCandidate(layout, remoteDefault)) {
      defaults.push(layout);
    } else {
      nonDefaults.push(layout);
    }
  }

  if (defaults.length === 0) return layouts;

  const mergedDefault = defaults.reduce((best, next) => pickNewerDashboardLayout(best, next));
  const defaultIds = new Set(
    defaults.flatMap((layout) =>
      layout.id === DASHBOARD_DEFAULT_LAYOUT_ID && remoteDefault?.id
        ? [layout.id, remoteDefault.id]
        : [layout.id]
    )
  );

  return [
    {
      ...mergedDefault,
      id: DASHBOARD_DEFAULT_LAYOUT_ID,
      isDefault: true,
    },
    ...nonDefaults.filter((layout) => !defaultIds.has(layout.id)),
  ];
}

function resolveRemoteLayoutForLocal(
  local: DashboardLayout,
  remoteLayouts: DashboardLayout[],
  remoteById: Map<string, DashboardLayout>,
  backendIdMap: Record<string, string>
): DashboardLayout | undefined {
  const direct = remoteById.get(local.id);
  if (direct) return direct;

  const mappedId = backendIdMap[local.id];
  if (mappedId) return remoteById.get(mappedId);

  if (local.id.startsWith("layout_")) {
    const nameMatches = remoteLayouts.filter(
      (layout) => !layout.isDefault && layout.name === local.name
    );
    if (nameMatches.length === 1) return nameMatches[0];
  }

  return undefined;
}

function findLocalAliasForRemoteId(
  remoteId: string,
  backendIdMap: Record<string, string>
): string | undefined {
  return Object.entries(backendIdMap).find(
    ([localId, mappedRemoteId]) => mappedRemoteId === remoteId && localId !== remoteId
  )?.[0];
}

function isRemoteLayoutMergedViaLocalAlias(
  remoteId: string,
  backendIdMap: Record<string, string>,
  mergedIds: Set<string>
): boolean {
  const localAlias = findLocalAliasForRemoteId(remoteId, backendIdMap);
  return Boolean(localAlias && mergedIds.has(localAlias));
}

export function rebuildBackendIdMapFromLayouts(
  layouts: DashboardLayout[],
  remoteLayouts: DashboardLayout[],
  existingMap: Record<string, string>
): Record<string, string> {
  const next = { ...existingMap };
  ensureDefaultLayoutIdMap(next, remoteLayouts);
  const remoteCustomByName = new Map(
    remoteLayouts
      .filter((layout) => !layout.isDefault)
      .map((layout) => [layout.name.toLowerCase(), layout.id] as const)
  );

  for (const layout of layouts) {
    if (!layout.id.startsWith("layout_")) continue;
    if (next[layout.id]) continue;
    const remoteId = remoteCustomByName.get(layout.name.toLowerCase());
    if (remoteId) {
      next[layout.id] = remoteId;
    }
  }

  return next;
}

export function remapActiveLayoutIdToCanonical(
  activeLayoutId: string | null,
  layouts: DashboardLayout[],
  backendIdMap: Record<string, string> = {}
): string | null {
  if (!activeLayoutId) return null;
  if (layouts.some((layout) => layout.id === activeLayoutId)) return activeLayoutId;

  const remoteDefault = findRemoteDefaultLayout(layouts);
  if (
    remoteDefault &&
    activeLayoutId === remoteDefault.id &&
    layouts.some((layout) => layout.id === DASHBOARD_DEFAULT_LAYOUT_ID)
  ) {
    return DASHBOARD_DEFAULT_LAYOUT_ID;
  }

  const localAlias = findLocalAliasForRemoteId(activeLayoutId, backendIdMap);
  if (localAlias && layouts.some((layout) => layout.id === localAlias)) {
    return localAlias;
  }

  return activeLayoutId;
}

/** Merge account-local and remote layouts without clobbering newer per-account edits. */
export function mergeDashboardLayoutsByRecency(
  localLayouts: DashboardLayout[],
  remoteLayouts: DashboardLayout[],
  backendIdMap: Record<string, string> = {}
): DashboardLayout[] {
  if (remoteLayouts.length === 0) return dedupeDashboardLayouts(localLayouts);
  if (localLayouts.length === 0) return dedupeDashboardLayouts(remoteLayouts);

  const remoteDefault = findRemoteDefaultLayout(remoteLayouts);
  const remoteById = new Map(remoteLayouts.map((layout) => [layout.id, layout]));
  const mergedIds = new Set<string>();
  const merged: DashboardLayout[] = [];

  for (const local of localLayouts) {
    if (isRemoteLayoutMergedViaLocalAlias(local.id, backendIdMap, mergedIds)) {
      mergedIds.add(local.id);
      continue;
    }

    let remote = resolveRemoteLayoutForLocal(local, remoteLayouts, remoteById, backendIdMap);
    if (!remote && isDefaultLayoutCandidate(local, remoteDefault) && remoteDefault) {
      remote = remoteDefault;
      mergedIds.add(remoteDefault.id);
    }
    const picked = remote ? pickNewerDashboardLayout(local, remote) : local;
    if (isDefaultLayoutCandidate(local, remoteDefault) && remoteDefault) {
      upsertCanonicalDefaultLayout(merged, picked);
      mergedIds.add(local.id);
      mergedIds.add(remoteDefault.id);
      mergedIds.add(DASHBOARD_DEFAULT_LAYOUT_ID);
      continue;
    }

    if (remote) {
      const keepLocalId =
        local.id.startsWith("layout_") ||
        Boolean(backendIdMap[local.id]) ||
        findLocalAliasForRemoteId(local.id, backendIdMap);
      merged.push(keepLocalId ? { ...picked, id: local.id } : picked);
      mergedIds.add(local.id);
      mergedIds.add(remote.id);
      continue;
    }

    mergedIds.add(local.id);
    merged.push(picked);
  }

  for (const remote of remoteLayouts) {
    if (!mergedIds.has(remote.id)) {
      if (remote.isDefault && merged.some((layout) => layout.id === DASHBOARD_DEFAULT_LAYOUT_ID)) {
        continue;
      }
      if (isRemoteLayoutMergedViaLocalAlias(remote.id, backendIdMap, mergedIds)) {
        continue;
      }
      merged.push(remote);
    }
  }

  return dedupeDashboardLayouts(merged);
}

/** Optional flags for adapters that mirror state to a remote API. */
export type DashboardLayoutPersistOptions = {
  /** When true, only update local fallback storage — do not PATCH/create/delete on the backend. */
  skipRemoteSync?: boolean;
};

export type DashboardLayoutRemoteHydrateState = {
  layouts: DashboardLayout[];
  activeLayoutId: string | null;
  backendIdMap?: Record<string, string>;
};

export interface DashboardLayoutPersistenceAdapter {
  loadLayouts: () => DashboardLayout[] | null;
  saveLayouts: (layouts: DashboardLayout[], options?: DashboardLayoutPersistOptions) => void;
  loadActiveLayoutId: () => string | null;
  saveActiveLayoutId: (layoutId: string, options?: DashboardLayoutPersistOptions) => void;
  hydrateFromRemote?: () => Promise<DashboardLayoutRemoteHydrateState | null>;
}

function canUseStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function createAccountScopedLocalStorageAdapter(
  scope: DashboardLayoutAccountScope
): DashboardLayoutPersistenceAdapter {
  const storageKeys = dashboardLayoutStorageKeys(scope);

  return {
    loadLayouts: () => {
      if (!canUseStorage()) return null;
      try {
        const raw = window.localStorage.getItem(storageKeys.layouts);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return null;
        return parsed.map((layout) => sanitizeLayout(layout));
      } catch {
        return null;
      }
    },
    saveLayouts: (layouts) => {
      if (!canUseStorage()) return;
      try {
        window.localStorage.setItem(storageKeys.layouts, JSON.stringify(layouts));
      } catch {
        // no-op: persistence failures should never break dashboard rendering
      }
    },
    loadActiveLayoutId: () => {
      if (!canUseStorage()) return null;
      try {
        return window.localStorage.getItem(storageKeys.activeLayoutId);
      } catch {
        return null;
      }
    },
    saveActiveLayoutId: (layoutId, _options) => {
      if (!canUseStorage()) return;
      try {
        window.localStorage.setItem(storageKeys.activeLayoutId, layoutId);
      } catch {
        // no-op
      }
    },
  };
}

/** @deprecated Use `createAccountScopedLocalStorageAdapter` — v1 keys were user-global. */
export const localStorageDashboardLayoutAdapter: DashboardLayoutPersistenceAdapter =
  createAccountScopedLocalStorageAdapter({
    brokerName: "__legacy__",
    brokerAccountCode: "global",
  });

function loadBackendIdMap(storageKey: string): Record<string, string> {
  if (!canUseStorage()) return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return Object.entries(parsed).reduce<Record<string, string>>((acc, [k, v]) => {
      if (typeof k === "string" && typeof v === "string") {
        acc[k] = v;
      }
      return acc;
    }, {});
  } catch {
    return {};
  }
}

function saveBackendIdMap(storageKey: string, map: Record<string, string>): void {
  if (!canUseStorage()) return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(map));
  } catch {
    // no-op
  }
}

export function createBackendDashboardLayoutAdapter(
  scope: DashboardLayoutAccountScope,
  fallbackAdapter: DashboardLayoutPersistenceAdapter = createAccountScopedLocalStorageAdapter(scope)
): DashboardLayoutPersistenceAdapter {
  const backendIdMapKey = dashboardLayoutStorageKeys(scope).backendIdMap;
  let syncTimer: number | null = null;
  let pendingLayouts: DashboardLayout[] | null = null;
  let isSyncInFlight = false;
  let lastActiveLayoutId: string | null = null;
  /** Do not enqueue remote sync until the first `hydrateFromRemote` attempt has finished (success or failure). */
  let initialRemotePullFinished = false;
  /** Layout snapshot saved while the initial remote hydrate was still in flight. */
  let pendingPreHydrateLayouts: DashboardLayout[] | null = null;

  const isConflictError = (error: unknown): boolean =>
    error instanceof Error && /\b409\b/.test(error.message);
  const isNotFoundError = (error: unknown): boolean =>
    error instanceof Error && /\b404\b/.test(error.message);

  const resolveRemoteId = (localLayoutId: string): string | null => {
    const map = loadBackendIdMap(backendIdMapKey);
    if (map[localLayoutId]) return map[localLayoutId];
    if (localLayoutId === DASHBOARD_DEFAULT_LAYOUT_ID) {
      return map[DASHBOARD_DEFAULT_LAYOUT_ID] ?? null;
    }
    // Local-only temp IDs should not be sent to backend until mapped.
    if (localLayoutId.startsWith("layout_")) return null;
    return localLayoutId;
  };

  const normalizeRemoteList = (remote: DashboardLayoutsListResponse): DashboardLayout[] =>
    (Array.isArray(remote.layouts) ? remote.layouts : []).map((layout) =>
      sanitizeLayout(layout)
    );
  const getVisibleLayoutItems = (layout: DashboardLayout) =>
    serializeDashboardLayoutItemsForApi(layout.items);

  const syncActiveLayoutToBackend = async () => {
    if (!isDashboardLayoutAccountScopeReady(scope)) return;
    if (!lastActiveLayoutId) return;
    const remoteId = resolveRemoteId(lastActiveLayoutId);
    if (!remoteId) return;
    try {
      await activateDashboardLayout(scope, remoteId);
    } catch (error) {
      console.warn("Failed to activate dashboard layout on backend.", error);
    }
  };

  const syncLayoutsToBackend = async (layouts: DashboardLayout[]) => {
    if (!isDashboardLayoutAccountScopeReady(scope)) return;
    if (isSyncInFlight) return;
    isSyncInFlight = true;
    try {
      let remote = await listDashboardLayouts(scope);
      let remoteLayouts = normalizeRemoteList(remote);
      const remoteById = new Map(remoteLayouts.map((layout) => [layout.id, layout]));
      const localById = new Map(layouts.map((layout) => [layout.id, layout]));
      const idMap = loadBackendIdMap(backendIdMapKey);
      ensureDefaultLayoutIdMap(idMap, remoteLayouts);
      const localRemoteIdSet = new Set<string>();
      const nextLocalLayouts: DashboardLayout[] = [];

      for (const layout of layouts) {
        const mappedId = resolveLayoutRemoteId(layout, idMap, remoteLayouts);
        if (!mappedId) {
          if (isLocalDefaultLayout(layout)) {
            console.warn("Skipping default dashboard layout sync until remote default id is known.");
            continue;
          }
          if (layout.id.startsWith("layout_")) {
            const created = await createDashboardLayout(scope, {
              name: layout.name,
              layoutVersion: layout.layoutVersion,
              items: getVisibleLayoutItems(layout),
            });
            nextLocalLayouts.push(
              sanitizeLayout({
                ...layout,
                id: layout.id,
                items: layout.items,
                layoutVersion: created.layoutVersion ?? layout.layoutVersion,
                updatedAt: created.updatedAt ?? layout.updatedAt,
              })
            );
            if (created?.id && created.id !== layout.id) {
              idMap[layout.id] = created.id;
              localRemoteIdSet.add(created.id);
            }
          }
          continue;
        }
        localRemoteIdSet.add(mappedId);
        const existing = remoteById.get(mappedId);
        if (existing) {
          try {
            const updated = await updateDashboardLayout(scope, mappedId, {
              ...(isLocalDefaultLayout(layout) ? {} : { name: layout.name }),
              layoutVersion: existing.layoutVersion,
              items: getVisibleLayoutItems(layout),
              isActive: layout.isActive,
            });
            nextLocalLayouts.push(
              sanitizeLayout({
                ...layout,
                id: layout.id,
                items: layout.items,
                layoutVersion: updated.layoutVersion ?? existing.layoutVersion,
                updatedAt: updated.updatedAt ?? layout.updatedAt,
              })
            );
          } catch (error) {
            if (isConflictError(error)) {
              remote = await listDashboardLayouts(scope);
              remoteLayouts = normalizeRemoteList(remote);
              const latest = remoteLayouts.find((r) => r.id === mappedId);
              if (latest) {
                const retried = await updateDashboardLayout(scope, mappedId, {
                  ...(isLocalDefaultLayout(layout) ? {} : { name: layout.name }),
                  layoutVersion: latest.layoutVersion,
                  items: getVisibleLayoutItems(layout),
                  isActive: layout.isActive,
                });
                nextLocalLayouts.push(
                  sanitizeLayout({
                    ...layout,
                    id: layout.id,
                    items: layout.items,
                    layoutVersion: retried.layoutVersion ?? latest.layoutVersion,
                    updatedAt: retried.updatedAt ?? layout.updatedAt,
                  })
                );
              } else {
                throw error;
              }
            } else {
              console.warn("Skipping dashboard layout update after non-retryable error.", error);
            }
          }
          continue;
        }

        if (isLocalDefaultLayout(layout)) {
          continue;
        }

        const created = await createDashboardLayout(scope, {
          name: layout.name,
          layoutVersion: layout.layoutVersion,
          items: getVisibleLayoutItems(layout),
        });
        nextLocalLayouts.push(
          sanitizeLayout({
            ...layout,
            id: layout.id,
            items: layout.items,
            layoutVersion: created.layoutVersion ?? layout.layoutVersion,
            updatedAt: created.updatedAt ?? layout.updatedAt,
          })
        );
        if (created?.id && created.id !== layout.id) {
          idMap[layout.id] = created.id;
        }
      }

      const mappedEntries = Object.entries(idMap);
      for (const [localId, remoteId] of mappedEntries) {
        if (localById.has(localId) || localById.has(remoteId)) {
          if (!localById.has(localId) && localById.has(remoteId) && localId !== remoteId) {
            delete idMap[localId];
          }
          continue;
        }
        try {
          await deleteDashboardLayout(scope, remoteId);
        } catch (error) {
          if (!isNotFoundError(error)) {
            console.warn("Failed to delete mapped dashboard layout on backend.", error);
            continue;
          }
        }
        delete idMap[localId];
      }

      for (const remoteLayout of remoteLayouts) {
        if (remoteLayout.isDefault) continue;
        if (localRemoteIdSet.has(remoteLayout.id)) continue;
        const localHasSameId = localById.has(remoteLayout.id);
        if (localHasSameId) continue;
        try {
          await deleteDashboardLayout(scope, remoteLayout.id);
        } catch (error) {
          if (!isNotFoundError(error)) {
            console.warn("Failed to delete stale dashboard layout on backend.", error);
          }
        }
      }

      saveBackendIdMap(backendIdMapKey, idMap);
      if (nextLocalLayouts.length > 0) {
        const localCurrent = fallbackAdapter.loadLayouts() ?? [];
        const localByCurrentId = new Map(localCurrent.map((layout) => [layout.id, layout]));
        nextLocalLayouts.forEach((layout) => {
          localByCurrentId.set(layout.id, layout);
        });
        fallbackAdapter.saveLayouts(Array.from(localByCurrentId.values()));
      }
      await syncActiveLayoutToBackend();
    } catch (error) {
      console.warn("Dashboard layout backend sync failed; keeping local fallback.", error);
    } finally {
      isSyncInFlight = false;
      if (pendingLayouts) {
        const next = pendingLayouts;
        pendingLayouts = null;
        await syncLayoutsToBackend(next);
      }
    }
  };

  const scheduleSync = (layouts: DashboardLayout[]) => {
    if (!isDashboardLayoutAccountScopeReady(scope)) return;
    pendingLayouts = layouts.map((layout) => ({
      ...layout,
      items: layout.items.map((item) => ({ ...item })),
    }));
    if (syncTimer != null) {
      window.clearTimeout(syncTimer);
    }
    syncTimer = window.setTimeout(() => {
      const next = pendingLayouts;
      pendingLayouts = null;
      syncTimer = null;
      if (next) {
        void syncLayoutsToBackend(next);
      }
    }, 150);
  };

  return {
    loadLayouts: () => fallbackAdapter.loadLayouts(),
    saveLayouts: (layouts, options) => {
      fallbackAdapter.saveLayouts(layouts, options);
      if (options?.skipRemoteSync) return;
      if (!initialRemotePullFinished) {
        pendingPreHydrateLayouts = layouts.map((layout) => ({
          ...layout,
          items: layout.items.map((item) => ({ ...item })),
        }));
        return;
      }
      scheduleSync(layouts);
    },
    loadActiveLayoutId: () => fallbackAdapter.loadActiveLayoutId(),
    saveActiveLayoutId: (layoutId, options) => {
      fallbackAdapter.saveActiveLayoutId(layoutId, options);
      if (options?.skipRemoteSync) return;
      if (!initialRemotePullFinished) return;
      if (lastActiveLayoutId === layoutId) return;
      lastActiveLayoutId = layoutId;
      const remoteId = resolveRemoteId(layoutId);
      if (!remoteId || !isDashboardLayoutAccountScopeReady(scope)) return;
      void activateDashboardLayout(scope, remoteId).catch((error) => {
        console.warn("Failed to activate dashboard layout on backend.", error);
      });
    },
    hydrateFromRemote: async () => {
      if (!isDashboardLayoutAccountScopeReady(scope)) {
        initialRemotePullFinished = true;
        return null;
      }
      try {
        const remote = await listDashboardLayouts(scope);
        const remoteLayouts = (Array.isArray(remote.layouts) ? remote.layouts : []).map((layout) =>
          sanitizeLayout(layout)
        );
        if (!remoteLayouts.length) return null;
        const localLayouts = fallbackAdapter.loadLayouts() ?? [];
        let idMap = loadBackendIdMap(backendIdMapKey);
        idMap = rebuildBackendIdMapFromLayouts(localLayouts, remoteLayouts, idMap);
        let merged = mergeDashboardLayoutsByRecency(localLayouts, remoteLayouts, idMap);
        // A save may land in localStorage while the list request was in flight — prefer it.
        const latestLocal = fallbackAdapter.loadLayouts() ?? [];
        merged = mergeDashboardLayoutsByRecency(latestLocal, merged, idMap);
        idMap = rebuildBackendIdMapFromLayouts(merged, remoteLayouts, idMap);
        saveBackendIdMap(backendIdMapKey, idMap);
        const activeLayoutId = remapActiveLayoutIdToCanonical(
          fallbackAdapter.loadActiveLayoutId() ?? remote.activeLayoutId ?? null,
          merged,
          idMap
        );
        return {
          layouts: merged,
          activeLayoutId,
          backendIdMap: idMap,
        };
      } catch (error) {
        console.warn("Failed to hydrate dashboard layouts from backend; using local fallback.", error);
        return null;
      } finally {
        initialRemotePullFinished = true;
        if (pendingPreHydrateLayouts) {
          pendingPreHydrateLayouts = null;
          window.setTimeout(() => {
            const latest = fallbackAdapter.loadLayouts() ?? [];
            if (latest.length > 0) {
              scheduleSync(latest);
            }
          }, 0);
        }
      }
    },
  };
}
